/**
 * O que cada evento do Histórico diz, em pt-BR (pedido da PoolTrack, 03/10: "Se
 * possível o detalhamento em cada histórico de edição. Ex: Vanessa editou o
 * telefone do cliente."). A edição vira uma linha por campo, "Telefone do
 * solicitante: (31) 98752-0000 → (31) 98752-4881", e nenhum evento mostra
 * chave crua do payload ("pausedMinutes: 0", "commentId: 12").
 *
 * PURO de propósito (sem React e sem alias `@/`): o web não tem executor de
 * testes, e a prova mora na API (apps/api/src/shared/ticket-history-detail.test.ts),
 * como a do description-edit.
 */
import {
  SERVICE_ORDER_TYPE_LABELS,
  SGR_ROUTINE_EVENT_SOURCE,
  TIFLUX_MIRROR_EVENT_SOURCE,
  TIFLUX_PULL_FIELD_LABELS,
  botActorOf,
  dispatchEventNotes,
  ticketSheetSlotLabel,
  ticketSheetSlotOfField,
  type AppointmentEditField,
  type ServiceOrderType,
  type Terms,
  type TicketEvent,
  type TicketFieldChange,
  type TifluxPullField,
} from '@erp/shared';
import { descriptionChangeOf, descriptionText } from './description-edit';

/** O vocabulário do tenant que o histórico usa (spec 017). */
export type HistoryTerms = Pick<Terms, 'contact' | 'resourceId' | 'resource'>;

/**
 * Uma linha do detalhe. `change` é "Rótulo: antes → depois", cada lado numa
 * linha só e com teto; `full` traz o texto inteiro dos dois lados quando algum
 * foi cortado — a tela abre com "Ver o antes e depois", como o erp#38 fazia com
 * a descrição. `text` é uma frase pronta ("Alterou: Telefone do solicitante").
 */
export type DetailLine =
  | {
      kind: 'change';
      label: string;
      from: string;
      to: string;
      full: { from: string | null; to: string | null } | null;
    }
  | { kind: 'text'; text: string };

const EMPTY = '—';
/** quantos caracteres cabem em cada lado da linha */
const SIDE_MAX = 60;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Os rótulos dos campos do evento `updated` — os mesmos nomes da tela. */
const UPDATED_FIELD_LABELS: Record<string, string> = {
  title: 'Título',
  description: 'Descrição',
  requestorName: 'Nome do solicitante',
  requestorEmail: 'E-mail do solicitante',
  requestorPhone: 'Telefone do solicitante',
  followers: 'Seguidores',
  catalogItem: 'Item de catálogo',
  // a chave dos eventos antigos do item de catálogo
  catalogItemId: 'Item de catálogo',
  group: 'Grupo',
  entities: 'Informações adicionais',
};

/**
 * Rótulo pt-BR de um campo do evento `updated`. O equipamento fala o termo do
 * tenant ("Veículo" na PoolTrack, "Recurso" no genérico), e a ficha do
 * associado também (spec 031: `sheet.phone` é "Telefone do associado"). Um
 * campo que esta versão da tela não conhece (a API sobe antes do SPA) aparece
 * pelo nome.
 */
export function updatedFieldLabel(field: string, terms: HistoryTerms): string {
  if (field === 'equipment') return cap(terms.resource);
  const slot = ticketSheetSlotOfField(field);
  if (slot) return ticketSheetSlotLabel(slot, terms);
  return UPDATED_FIELD_LABELS[field] ?? field;
}

/** "(da descrição)" — de onde veio o valor efetivo da ficha num lado da mudança (spec 031) */
const SHEET_SOURCE_SUFFIX: Record<string, string> = {
  description: 'da descrição',
  title: 'do título',
  requestor: 'do solicitante',
  equipment: 'do recurso',
  // spec 032: o valor que a pessoa aceitou do SGR (a escolha, o "usar", o "Atualizar")
  sgr: 'do cadastro de origem',
};

/** Texto numa linha só: quebras e espaços repetidos viram um espaço. */
function oneLine(v: string): string {
  return v.replace(/\s+/g, ' ').trim();
}

/** Com teto; `cut` diz que a linha não mostra tudo (cortou, ou juntou linhas). */
function clip(v: string, max = SIDE_MAX): { text: string; cut: boolean } {
  const one = oneLine(v);
  if (one.length <= max) return { text: one, cut: /\n/.test(v.trim()) };
  return { text: `${one.slice(0, max - 1)}…`, cut: true };
}

/** "2026-10-03" → "03/10/2026" (data de campo personalizado, sem fuso). */
function isoDay(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}

/** O valor como a pessoa o leria na tela, ou null quando vazio. */
function readable(change: Pick<TicketFieldChange, 'field' | 'fieldType'>, v: string | null): string | null {
  if (v === null || v.trim() === '') return null;
  if (change.field === 'description') return descriptionText(v);
  if (change.fieldType === 'checkbox') return v === 'true' ? 'Sim' : v === 'false' ? 'Não' : v;
  if (change.fieldType === 'date') return isoDay(v);
  return v;
}

/** Uma linha de mudança: rótulo, os dois lados com teto, e o inteiro se algo foi cortado. */
function changeLine(
  label: string,
  change: Pick<TicketFieldChange, 'field' | 'fieldType' | 'fromSource' | 'toSource'>,
  from: string | null,
  to: string | null
): DetailLine {
  // a ficha (spec 031): o lado que NÃO é da pessoa diz de onde veio —
  // "31 9… (da descrição) → 31 9…"; "voltar ao da descrição" é "→ X (da descrição)"
  const sourced = (v: string | null, source: string | null | undefined) =>
    v !== null && v.trim() !== '' && source && SHEET_SOURCE_SUFFIX[source] ? `${v} (${SHEET_SOURCE_SUFFIX[source]})` : v;
  const a = readable(change, sourced(from, change.fromSource));
  const b = readable(change, sourced(to, change.toSource));
  // descrição e texto livre longo entre aspas, como o apontamento editado faz
  const quote = change.field === 'description';
  const side = (v: string | null) => {
    if (v === null) return { text: EMPTY, cut: false };
    const c = clip(v);
    return { text: quote ? `“${c.text}”` : c.text, cut: c.cut };
  };
  const x = side(a);
  const y = side(b);
  return {
    kind: 'change',
    label,
    from: x.text,
    to: y.text,
    full: x.cut || y.cut ? { from: a, to: b } : null,
  };
}

/** As mudanças gravadas (o formato deste pedido), ou null quando o evento é anterior. */
export function changesOf(payload: Record<string, unknown>): TicketFieldChange[] | null {
  const raw = payload['changes'];
  if (!Array.isArray(raw)) return null;
  const side = (v: unknown) => (typeof v === 'string' ? v : null);
  const out: TicketFieldChange[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const c = item as Record<string, unknown>;
    if (typeof c['field'] !== 'string') continue;
    out.push({
      field: c['field'],
      from: side(c['from']),
      to: side(c['to']),
      ...(typeof c['label'] === 'string' ? { label: c['label'] } : {}),
      ...(typeof c['fieldType'] === 'string' ? { fieldType: c['fieldType'] } : {}),
      ...(typeof c['fieldId'] === 'number' ? { fieldId: c['fieldId'] } : {}),
      ...(typeof c['fromSource'] === 'string' ? { fromSource: c['fromSource'] } : {}),
      ...(typeof c['toSource'] === 'string' ? { toSource: c['toSource'] } : {}),
    });
  }
  return out;
}

/** "Alterou: Grupo (agora: Suporte)" — os eventos antigos só gravavam o valor novo. */
function nowOnly(label: string, v: unknown): DetailLine {
  const value = typeof v === 'string' && v.trim() !== '' ? clip(v).text : EMPTY;
  return { kind: 'text', text: `Alterou: ${label} (agora: ${value})` };
}

/**
 * As linhas do evento `updated`:
 * - com `changes` (daqui em diante): uma por campo, "Rótulo: antes → depois";
 *   o campo personalizado aparece pelo rótulo que tinha quando foi editado;
 * - só com `fields` (os anteriores): "Alterou: Telefone do solicitante", e a
 *   descrição com o antes e o depois quando o evento os traz (erp#38);
 * - os antigos de item de catálogo, equipamento e grupo: o campo e o valor novo.
 */
export function updatedEventLines(payload: Record<string, unknown>, terms: HistoryTerms): DetailLine[] {
  const lines: DetailLine[] = [];
  const changes = changesOf(payload);
  if (changes && changes.length > 0) {
    for (const c of changes) {
      const label = c.field === 'entities' ? (c.label ?? updatedFieldLabel(c.field, terms)) : updatedFieldLabel(c.field, terms);
      lines.push(changeLine(label, c, c.from, c.to));
    }
  } else if (Array.isArray(payload['fields'])) {
    const description = descriptionChangeOf(payload);
    const named = payload['fields']
      .filter((f): f is string => typeof f === 'string')
      .filter((f) => !(f === 'description' && description));
    if (named.length > 0)
      lines.push({ kind: 'text', text: `Alterou: ${named.map((f) => updatedFieldLabel(f, terms)).join(', ')}` });
    if (description)
      lines.push(changeLine(updatedFieldLabel('description', terms), { field: 'description' }, description.from, description.to));
  } else if ('catalogItemId' in payload) {
    lines.push({ kind: 'text', text: `Alterou: ${updatedFieldLabel('catalogItemId', terms)}` });
  } else if ('equipment' in payload) {
    lines.push(nowOnly(updatedFieldLabel('equipment', terms), payload['equipment']));
  } else if ('group' in payload) {
    lines.push(nowOnly(updatedFieldLabel('group', terms), payload['group']));
  }
  const origin = originText(payload);
  if (origin) lines.push({ kind: 'text', text: origin });
  return lines;
}

/** A linha como texto corrido (a timeline da visão compacta é uma linha por evento). */
export function lineText(line: DetailLine): string {
  return line.kind === 'text' ? line.text : `${line.label}: ${line.from} → ${line.to}`;
}

// ── os outros tipos ─────────────────────────────────────────────────────────

const VIA_LABELS: Record<string, string> = {
  portal: 'pelo portal do cliente',
  import: 'importado do TiFlux',
  automation: 'por automação',
  email: 'por e-mail',
  form: 'pelo formulário externo',
  scheduled: 'por atividade agendada',
  api: 'pela API',
  // pedido da PoolTrack (05/10): o envio do disparo lança o 1º contato
  dispatch: '1º contato pelo disparo do WhatsApp',
};

/** De onde veio a ação: "por automação", "puxado do TiFlux"… — nunca a chave crua. */
function originText(p: Record<string, unknown>): string | null {
  if (p['source'] === 'tiflux_pull') return 'puxado do TiFlux';
  const via = p['via'];
  return typeof via === 'string' ? (VIA_LABELS[via] ?? null) : null;
}

/** "01:30" — a mesma forma da tabela de apontamentos (formatMinutes do web). */
function hhmm(min: number): string {
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const EVENT_FIELD_LABEL: Partial<Record<TicketEvent['type'], string>> = {
  stage_changed: 'Estágio',
  status_changed: 'Status',
  priority_changed: 'Prioridade',
  responsible_changed: 'Responsável',
  technician_changed: 'Técnico',
  reparented: 'Cliente',
  desk_moved: 'Mesa',
  dispatch_sent: 'Estágio',
  dispatch_failed: 'Estágio',
  dispatch_replied: 'Estágio',
};

function partName(v: unknown): string {
  if (v === null || v === undefined || v === '') return EMPTY;
  if (typeof v === 'object') {
    const name = (v as Record<string, unknown>)['name'];
    if (name !== undefined && name !== null) return String(name);
    return EMPTY;
  }
  return String(v);
}

const text = (t: string): DetailLine => ({ kind: 'text', text: t });

/**
 * As linhas de qualquer evento. Cada tipo diz o que o payload dele significa;
 * chave que nenhum tipo conhece (ids, flags internas) não aparece.
 */
export function eventDetailLines(
  event: Pick<TicketEvent, 'type' | 'payload'>,
  terms: HistoryTerms
): DetailLine[] {
  const p = event.payload;
  const origin = originText(p);
  const withOrigin = (lines: DetailLine[]) => (origin ? [...lines, text(origin)] : lines);
  switch (event.type) {
    case 'updated':
      return updatedEventLines(p, terms);
    // spec 022: o texto é o próprio comentário, e a chave já sai como autor
    case 'bot_note_added':
      return [text('nota interna — o texto está em Comunicação')];
    // spec 026: o resumo do "Puxar do TiFlux"
    case 'tiflux_pulled': {
      const line = tifluxPulledDetail(p);
      return line ? [text(line)] : [];
    }
    // spec 043: o espelho encerrou aqui o que já tinha acabado lá
    case 'tiflux_closed': {
      const line = tifluxClosedDetail(p);
      return line ? [text(line)] : [];
    }
    // pedido da PoolTrack (02/10): início, fim, duração e descrição, antes → depois
    case 'appointment_edited': {
      const line = appointmentEditedDetail(p);
      return line ? [text(line)] : [];
    }
    // spec 016: "OS nº 3 — Instalação"
    case 'service_order_created': {
      const type = p['type'] as ServiceOrderType | undefined;
      const label = type && type in SERVICE_ORDER_TYPE_LABELS ? SERVICE_ORDER_TYPE_LABELS[type] : null;
      return [text([`OS nº ${String(p['number'] ?? '?')}`, label].filter(Boolean).join(' — '))];
    }
    // spec 014: {from,to} quando o estágio mudou, reason e chatId — ids e
    // códigos crus não dizem nada ao atendente
    case 'dispatch_sent':
    case 'dispatch_failed':
    case 'dispatch_replied': {
      const parts: string[] = [];
      if ('from' in p || 'to' in p) parts.push(`Estágio: ${partName(p['from'])} → ${partName(p['to'])}`);
      // o motivo (sem telefone: onde se procurou) e de onde veio o número
      parts.push(...dispatchEventNotes(p, terms));
      if (p['manual'] === true) parts.push('disparo manual');
      return parts.length > 0 ? [text(parts.join(' · '))] : [];
    }
    // spec 054: o motivo (o nome na hora do fechamento) e a observação de quem fechou
    case 'closed': {
      const lines: DetailLine[] = [];
      if (typeof p['reason'] === 'string' && p['reason'] !== '') lines.push(text(`Motivo: ${p['reason']}`));
      if (typeof p['note'] === 'string' && p['note'].trim() !== '')
        lines.push(text(`Observação: ${clip(p['note']).text}`));
      return withOrigin(lines);
    }
    case 'created': {
      const title = typeof p['title'] === 'string' && p['title'].trim() !== '' ? p['title'] : null;
      return withOrigin(title ? [text(`Título: ${clip(title).text}`)] : []);
    }
    case 'comment_added': {
      const kind = p['internal'] === true ? 'interno' : p['internal'] === false ? 'público' : null;
      return withOrigin(kind ? [text(kind)] : []);
    }
    // a duração do apontamento (o id dele não diz nada)
    case 'appointment_added':
    case 'timer_stopped':
      return withOrigin(typeof p['minutes'] === 'number' ? [text(`Duração: ${hhmm(p['minutes'])}`)] : []);
    // o 1º contato que o envio lançou e desfez: o disparo voltou para a fila
    // (adiado pela Blip) sem nada ter chegado ao contato
    case 'appointment_deleted':
      return withOrigin(
        p['reason'] === 'dispatch_requeued'
          ? [text(`o disparo voltou para a fila — nada chegou ao ${terms.contact}`)]
          : []
      );
    // SLA retomado: quanto a pausa durou, em minutos ÚTEIS da mesa (spec §4)
    case 'sla_resumed': {
      const m = p['pausedMinutes'];
      if (typeof m !== 'number') return withOrigin([]);
      const span = Math.round(m) < 1 ? 'menos de 1 min' : hhmm(m);
      return withOrigin([text(`Em pausa por ${span} (horário útil)`)]);
    }
    case 'file_added':
    case 'file_deleted': {
      const name = typeof p['fileName'] === 'string' && p['fileName'] !== '' ? p['fileName'] : null;
      return withOrigin(name ? [text(`Arquivo: ${clip(name).text}`)] : []);
    }
    default:
      break;
  }
  // os que levam antes → depois (estágio, status, prioridade, responsável,
  // técnico, cliente, mesa) e os que só dizem de onde vieram (automação,
  // "Puxar do TiFlux": fechado, cancelado, reaberto…)
  const lines: DetailLine[] = [];
  if ('from' in p || 'to' in p) {
    const prefix = EVENT_FIELD_LABEL[event.type];
    const arrow = `${partName(p['from'])} → ${partName(p['to'])}`;
    lines.push(text(prefix ? `${prefix}: ${arrow}` : arrow));
  }
  // troca de cliente que soltou o equipamento (ele não era do cliente novo)
  if (event.type === 'reparented' && 'equipmentCleared' in p) {
    const name = typeof p['equipmentCleared'] === 'string' && p['equipmentCleared'] !== '' ? p['equipmentCleared'] : null;
    lines.push(text(`${cap(terms.resource)}: ${name ? `${name} → ${EMPTY}` : '(removido)'}`));
  }
  return withOrigin(lines);
}

/** O detalhe numa linha só (" · " entre as partes), ou null quando não há o que dizer. */
export function eventDetail(event: Pick<TicketEvent, 'type' | 'payload'>, terms: HistoryTerms): string | null {
  const lines = eventDetailLines(event, terms);
  return lines.length > 0 ? lines.map(lineText).join(' · ') : null;
}

/**
 * "Etapa: 4-Em contato → 7.1-Agendado · 2 comentários · 1 apontamento ·
 * mantido no ERP: responsável" — o payload do evento `tiflux_pulled` (spec 026).
 */
function tifluxPulledDetail(p: Record<string, unknown>): string | null {
  const fieldLabel = (f: unknown) =>
    typeof f === 'string' && f in TIFLUX_PULL_FIELD_LABELS ? TIFLUX_PULL_FIELD_LABELS[f as TifluxPullField] : String(f);
  const parts: string[] = [];
  const applied = Array.isArray(p['applied']) ? (p['applied'] as Array<Record<string, unknown>>) : [];
  for (const a of applied) parts.push(`${fieldLabel(a['field'])}: ${partName(a['from'])} → ${partName(a['to'])}`);
  const comments = typeof p['comments'] === 'number' ? p['comments'] : 0;
  if (comments > 0) parts.push(`${comments} ${comments === 1 ? 'comentário' : 'comentários'}`);
  const appts = (p['appointments'] ?? {}) as {
    inserted?: number;
    updated?: number;
    accepted?: number;
    kept?: number;
  };
  const nAppts = (appts.inserted ?? 0) + (appts.updated ?? 0);
  if (nAppts > 0) parts.push(`${nAppts} ${nAppts === 1 ? 'apontamento' : 'apontamentos'}`);
  // ADR-013: a decisão nos apontamentos editados no ERP que mudaram no TiFlux
  const accepted = appts.accepted ?? 0;
  if (accepted > 0)
    parts.push(`${accepted} ${accepted === 1 ? 'apontamento aceito' : 'apontamentos aceitos'} do TiFlux`);
  const keptAppts = appts.kept ?? 0;
  if (keptAppts > 0)
    parts.push(`${keptAppts} ${keptAppts === 1 ? 'apontamento mantido' : 'apontamentos mantidos'} no ERP`);
  const kept = Array.isArray(p['kept']) ? (p['kept'] as Array<Record<string, unknown>>) : [];
  if (kept.length > 0) parts.push(`mantido no ERP: ${kept.map((k) => fieldLabel(k['field']).toLowerCase()).join(', ')}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * "Fechado no TiFlux (nº 160001)" — o payload do `tiflux_closed` (spec 043).
 * O apagado lá (404) diz o que aconteceu aqui: o ticket foi cancelado no ERP.
 */
function tifluxClosedDetail(p: Record<string, unknown>): string | null {
  const what =
    p['outcome'] === 'closed'
      ? 'Fechado no TiFlux'
      : p['outcome'] === 'canceled'
        ? 'Cancelado no TiFlux'
        : p['outcome'] === 'deleted'
          ? 'Não existe mais no TiFlux'
          : null;
  if (!what) return null;
  const n = p['tifluxTicketNumber'];
  const there = typeof n === 'number' ? `${what} (nº ${n} lá)` : what;
  return p['outcome'] === 'deleted' ? `${there} — cancelado aqui` : there;
}

const APPOINTMENT_FIELD_LABELS: Record<AppointmentEditField, string> = {
  startedAt: 'Início',
  endedAt: 'Fim',
  description: 'Descrição',
};

/** "13/08 10:00" — o instante no relógio de quem lê, como a tabela de apontamentos */
function dayAndTime(d: Date): { day: string; time: string } {
  return {
    day: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    time: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
  };
}

/** "13/08 10:00 → 09:30" (mesmo dia) ou "13/08 10:00 → 14/08 09:30" */
function instantChange(from: unknown, to: unknown): string {
  const a = typeof from === 'string' ? new Date(from) : null;
  const b = typeof to === 'string' ? new Date(to) : null;
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${partName(from)} → ${partName(to)}`;
  const x = dayAndTime(a);
  const y = dayAndTime(b);
  return x.day === y.day ? `${x.day} ${x.time} → ${y.time}` : `${x.day} ${x.time} → ${y.day} ${y.time}`;
}

/** o texto da descrição numa linha só, com teto — o histórico é uma linha por evento */
function quoted(v: unknown, max = SIDE_MAX): string {
  if (typeof v !== 'string' || v.trim() === '') return EMPTY;
  return `“${clip(v, max).text}”`;
}

/**
 * "Início: 13/08 10:00 → 09:30 · Duração: 01:00 → 01:30 · Descrição: “visita” →
 * “visita técnica”" — o payload do evento `appointment_edited`.
 */
function appointmentEditedDetail(p: Record<string, unknown>): string | null {
  const changes = Array.isArray(p['changes']) ? (p['changes'] as Array<Record<string, unknown>>) : [];
  const times: string[] = [];
  const texts: string[] = [];
  for (const c of changes) {
    const field = c['field'];
    if (field === 'startedAt' || field === 'endedAt')
      times.push(`${APPOINTMENT_FIELD_LABELS[field]}: ${instantChange(c['from'], c['to'])}`);
    else if (field === 'description')
      texts.push(`${APPOINTMENT_FIELD_LABELS.description}: ${quoted(c['from'])} → ${quoted(c['to'])}`);
  }
  // a duração logo depois dos horários que a mudaram
  const m = (p['minutes'] ?? null) as { from?: unknown; to?: unknown } | null;
  if (m && typeof m.from === 'number' && typeof m.to === 'number')
    times.push(`Duração: ${hhmm(m.from)} → ${hhmm(m.to)}`);
  // ADR-013: o apontamento veio do TiFlux — daqui em diante vale o do ERP
  const origin = p['fromTiflux'] === true ? ['veio do TiFlux: agora vale o do ERP'] : [];
  const parts = [...times, ...texts, ...origin];
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Quem fez (spec 022): a pessoa; sem pessoa, o bot pela chave (o payload leva
 * o nome dela); a rotina noturna do Recurso (spec 035, D4), que não é usuário;
 * o espelho do TiFlux (spec 043); senão, o sistema (automação, disparo).
 */
export function eventActorName(event: Pick<TicketEvent, 'userName' | 'payload'>): string {
  if (event.userName) return event.userName;
  if (event.payload?.source === SGR_ROUTINE_EVENT_SOURCE) return 'Rotina diária';
  if (event.payload?.source === TIFLUX_MIRROR_EVENT_SOURCE) return 'Espelho do TiFlux';
  const bot = botActorOf(event.payload);
  return bot ? `Bot · chave "${bot.keyName}"` : 'Sistema';
}
